import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { LoadingState } from '../../utils/loadingState';
import { poolCertificateApi } from 'Api/Pools/poolCertificateApi';
import { PoolSafetyCertificateResult } from 'Api/Pools/Types/types';
import { useQuery } from '@tanstack/react-query';
import { CertificateInfo } from '@urbanx/agx-ui-components';
import { useContext } from 'react';
import { AuthContext } from '@urbanx/agx.auth.client';

interface PoolCertificateState {
  loadingState: LoadingState;
  poolSafetyCertificate?: CertificateInfo | null;
  buildingCertificate?: CertificateInfo | null;
}

const initialState = {
  loadingState: LoadingState.NotLoaded,
} satisfies PoolCertificateState as PoolCertificateState;

const slice = createSlice({
  name: 'poolDocumentation',
  initialState,
  reducers: {
    startLoadingPoolCertificate: state => {
      return {
        ...state,
        ...initialState,
        loadingState: LoadingState.Loading,
      };
    },
    poolCertificateLoaded: (
      state,
      action: PayloadAction<{
        poolSafetyCertificate?: CertificateInfo | null;
        buildingCertificate?: CertificateInfo | null;
      }>
    ) => {
      const { poolSafetyCertificate, buildingCertificate } = action.payload;

      return {
        ...state,
        poolSafetyCertificate,
        buildingCertificate,
        loadingState: LoadingState.Loaded,
      };
    },
    errorLoadingPoolCertificate: state => {
      return {
        ...state,
        ...initialState,
        loadingState: LoadingState.Failed,
      };
    },
    clearPoolCertificateData: state => {
      return {
        ...state,
        ...initialState,
      };
    },
  },
});

export default slice.reducer;

const {
  startLoadingPoolCertificate,
  poolCertificateLoaded,
  errorLoadingPoolCertificate,
  clearPoolCertificateData,
} = slice.actions;

export {
  startLoadingPoolCertificate,
  poolCertificateLoaded,
  errorLoadingPoolCertificate,
  clearPoolCertificateData,
};

const fetchPoolCertificate = async (
  getAuthToken: () => Promise<string | void>,
  campaignId: string,
  lot?: string,
  planType?: string,
  planNumber?: string
) => {
  const token = await getAuthToken();

  if (!token) return;

  const plan = `${planType}/${planNumber}`;
  const result = await poolCertificateApi(
    token
  ).get<PoolSafetyCertificateResult>('GetPoolSafetyCertificate', {
    campaignId: campaignId,
    state: 'Queensland',
    country: 'Australia',
    lotNumber: lot,
    planNumber: plan,
  });

  return result.data || {};
};

export const useGetPoolSafetyCertificate = (
  campaignId: string,
  lot?: string,
  planType?: string,
  planNumber?: string
) => {
  const { getAuthToken } = useContext(AuthContext);

  const { data, isError, isPending } = useQuery<
    PoolSafetyCertificateResult | undefined
  >({
    queryKey: [
      'GetPoolSafetyCertificate',
      campaignId,
      lot,
      planType,
      planNumber,
    ],
    queryFn: ctx =>
      fetchPoolCertificate(
        getAuthToken,
        ctx.queryKey[1] as string,
        ctx.queryKey[2] as string,
        ctx.queryKey[3] as string,
        ctx.queryKey[4] as string
      ),
    enabled:
      !!getAuthToken && !!campaignId && !!lot && !!planType && !!planNumber,
  });

  return {
    data,
    isError,
    isPending,
  };
};
